import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import TriskelionMark from "./TriskelionMark";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { T } from "@/constants/testIds";

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenu(false), [location.pathname]);

  const goHeritage = () => {
    setMenu(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById("heritage")?.scrollIntoView({ behavior: "smooth" }), 400);
    } else {
      document.getElementById("heritage")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-stone-200 bg-[#FAF8F4]/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid={T.navLogo} className="flex items-center gap-3">
          <img src="/logo.png" alt="V.I.A logo" className="h-10 w-10 object-cover" />
          <span className="leading-none">
            <span className="block font-display text-2xl font-black uppercase tracking-wide text-stone-950">V.I.A</span>
            <span className="mt-1 block font-mono text-[8px] uppercase tracking-[0.3em] text-stone-500">
              Vannin Independent Apparel
            </span>
          </span>
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          <Link
            data-testid={T.navShop}
            to="/shop"
            className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-stone-700 transition-colors hover:text-[#E52E39]"
          >
            Shop
          </Link>
          <button
            data-testid={T.navHeritage}
            onClick={goHeritage}
            className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-stone-700 transition-colors hover:text-[#E52E39]"
          >
            Heritage
          </button>
          <Link
            data-testid={T.navAccount}
            to="/account"
            className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-stone-700 transition-colors hover:text-[#E52E39]"
          >
            {user ? "Account" : "Sign in"}
          </Link>
          <span className="border border-stone-300 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#C79500]">
            £ GBP
          </span>
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid={T.navCart}
            onClick={() => setOpen(true)}
            className="relative flex h-11 w-11 items-center justify-center border border-stone-300 text-stone-950 transition-colors hover:border-[#E52E39] hover:text-[#E52E39]"
            aria-label="Open bag"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center bg-[#E52E39] px-1 font-mono text-[10px] font-bold text-white">
                {count}
              </span>
            )}
          </button>
          <button
            onClick={() => setMenu(!menu)}
            className="flex h-11 w-11 items-center justify-center border border-stone-300 text-stone-950 md:hidden"
            aria-label="Menu"
          >
            {menu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b border-stone-200 bg-[#FAF8F4]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              <Link to="/shop" className="px-2 py-3 font-display text-xl font-bold uppercase tracking-wide text-stone-950">
                Shop
              </Link>
              <Link to="/account" className="px-2 py-3 font-display text-xl font-bold uppercase tracking-wide text-stone-950">
                {user ? "Account" : "Sign in"}
              </Link>
              <button
                onClick={goHeritage}
                className="px-2 py-3 text-left font-display text-xl font-bold uppercase tracking-wide text-stone-950"
              >
                Heritage
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
